import type {
  CurrentUserMe,
  UserCreate,
  UserHistoryResponse,
  UserProfessionResponse,
  UserProfessionUpdateRequest,
  UserUsageData,
} from '@/types/users-types';
import ApiService, { type ApiResponse } from './api-services';

class UserServices extends ApiService {
  constructor() {
    super('/users');
  }

  createUser(user: UserCreate): ApiResponse {
    return this.postData('', user);
  }

  getUserHistory(): ApiResponse<UserHistoryResponse> {
    return this.postData('/history', {});
  }

  deleteUserHistory(type: string, id: string): ApiResponse {
    return this.deleteData(`/history/delete/${type}/${id}`, {});
  }

  clearUserHistory(type: string): ApiResponse {
    return this.deleteData(`/history/clear/${type}`, {});
  }

  getUserUsage(): ApiResponse<UserUsageData> {
    return this.getData('/usage');
  }

  deleteUser(userId: string): ApiResponse {
    const body = {
      userId,
    };
    return this.deleteData('/delete', body);
  }

  updateUserProfession(profession: UserProfessionUpdateRequest): ApiResponse {
    return this.postData('/update-profession', profession);
  }

  getUserProfession(): ApiResponse<UserProfessionResponse> {
    return this.getData('/get-profession');
  }

  /**
   * Explicit on/off switch for the advocate practice surface (#6324). Backs the
   * "Zapnúť / Vypnúť Moja kancelária" control. Owner-scoped on the backend
   * (user_id from the auth session). Selecting *Advokát* latches it on
   * automatically; this is the only path that turns the latched flag back off.
   */
  setPracticeEnabled(
    enabled: boolean
  ): ApiResponse<{ practice_self_enabled: boolean }> {
    return this.patchData('/practice-enabled', { enabled });
  }

  /**
   * `GET /users/me`.
   *
   * `options.accessToken` (#11369) exists for exactly one caller:
   * `McpConsentInterstitial`, which lives OUTSIDE the `(consent-manger)`
   * layout group that mounts `SessionTokenSync`, so the `apiClient` request
   * interceptor's `sessionStorage`-derived `Authorization` header is
   * commonly absent there (`sessionStorage` is per-tab, and the OAuth
   * handshake routinely lands in a fresh tab). That component reads the
   * token straight off the Supabase browser client and passes it through.
   * Omitting the argument reproduces the pre-#11369 behaviour byte for byte,
   * which is what every other call site does.
   *
   * The headers are SPREAD over `this.getHeaders()` rather than replacing
   * it — `ApiService.getData` does `options?.headers || this.getHeaders()`,
   * so passing a bare `{ Authorization }` would silently drop the default
   * `Content-type` (same trap `mcp-attestation-service.ts` documents).
   */
  getCurrentUser(options?: {
    accessToken?: string | undefined;
  }): ApiResponse<CurrentUserMe | null> {
    if (options?.accessToken === undefined) {
      return this.getData('/me');
    }
    return this.getData('/me', {
      headers: {
        ...this.getHeaders(),
        Authorization: `Bearer ${options.accessToken}`,
      },
    });
  }

  updateLastPerspective(perspective: string): ApiResponse {
    return this.postData('/update-last-perspective', { perspective });
  }

  getLastPerspective(): ApiResponse<string | null> {
    return this.getData('/get-last-perspective');
  }

  dismissOnboarding(): ApiResponse {
    return this.postData('/me/dismiss-onboarding', {});
  }
}
export default new UserServices();
